import { useState } from 'react';

import { useBoundStore } from 'app/store';
import { ResidentType } from 'pages/NewConfirmation/config';
import { BottomButton } from 'shared/ui/Button/BottomButton';
import { IconTitleDescButton } from 'shared/ui/onBoarding';
import { ReactComponent as SuccessIcon } from 'styles/images/icon_success.svg';
import { ReactComponent as WarningIcon } from 'styles/images/onboarding-warning-icon.svg';

import { usePostKareNotePatients } from '../model';

export const PtInfoCheck = () => {
  const { mutate: postKareNotePatients } = usePostKareNotePatients();
  const { hspNm, hspId, ptNm, ptBrdy, usrId, nativeGb } = useBoundStore((state) => state['kareNote'].serviceParams);

  const formattedPtBrdy = ptBrdy?.slice(2, 8);

  const [usrNm, setUsrNm] = useState(ptNm || '');
  const [usrRrnBackRealValue, setUsrRrnBackRealValue] = useState('');
  const [usrRrnBackDisplayValue, setUsrRrnBackDisplayValue] = useState('');

  const [isResult, setIsResult] = useState('');
  const [successRedirectUrl, setSuccessRedirectUrl] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const currentUrl = window.location.href;

  const handlePostKareNotePatients = () => {
    const editedUsrNm = usrNm.trim();
    const editedPtRrn = formattedPtBrdy + usrRrnBackRealValue;

    postKareNotePatients(
      {
        hspId,
        ptRrn: editedPtRrn,
        usrNm: editedUsrNm,
        usrId,
      },
      {
        onSuccess: (response) => {
          if (response.data.result === '0') {
            setIsResult('success');
            setSuccessRedirectUrl(response.data.successRedirectUrl);
          } else {
            setIsResult('failed');
          }
        },
        onError: () => {
          setIsResult('failed');
        },
      },
    );
  };

  const handleRrnBackChange = (e) => {
    const inputValue = e.target.value;
    const cursorPosition = e.target.selectionStart ?? inputValue.length;
    const prevLength = usrRrnBackRealValue.length;
    const currentLength = inputValue.length;

    let updatedValue;

    if (currentLength > prevLength) {
      const addedCount = currentLength - prevLength;
      const insertPosition = cursorPosition - addedCount;
      const addedPart = inputValue.slice(insertPosition, cursorPosition);
      const newNumbers = addedPart.replace(/[^0-9]/g, '');

      updatedValue =
        usrRrnBackRealValue.slice(0, insertPosition) + newNumbers + usrRrnBackRealValue.slice(insertPosition);
    } else if (currentLength < prevLength) {
      const deleteCount = prevLength - currentLength;
      updatedValue =
        usrRrnBackRealValue.slice(0, cursorPosition) + usrRrnBackRealValue.slice(cursorPosition + deleteCount);
    } else {
      updatedValue = usrRrnBackRealValue;
    }

    const limited = updatedValue.slice(0, 20);
    setUsrRrnBackRealValue(limited);
    setUsrRrnBackDisplayValue(showPassword ? limited : '*'.repeat(limited.length));
  };

  const isForeigner = nativeGb === ResidentType.Foreigner;
  const btnDisabled = usrNm.length <= 0 || usrRrnBackRealValue.length < 7;

  if (isResult === 'success')
    return (
      <IconTitleDescButton
        title={<>환자 정보를 확인했어요</>}
        desc={
          <>
            이제 진료·검사 이력 등 중요한 건강 정보를
            <br />
            한곳에서 편하게 확인해 보세요.
          </>
        }
        icon={<SuccessIcon />}
        buttons={[
          {
            label: '진료·검사 이력보기',
            variant: 'primary',
            onClick: () => {
              window.location.replace(successRedirectUrl);
            },
          },
        ]}
      />
    );

  if (isResult === 'failed')
    return (
      <IconTitleDescButton
        title={
          <>
            환자 정보를
            <br />
            확인할 수 없어요
          </>
        }
        desc={
          <>
            입력하신 정보가 일치하지 않거나 <br />
            환자 정보 확인 과정에서 문제가 발생했어요. <br />
            잠시 후 다시 시도해 주세요.
          </>
        }
        icon={<WarningIcon />}
        buttons={[
          {
            label: '닫기',
            variant: 'secondary',
            onClick: () => {
              if (/android/i.test(navigator.userAgent)) {
                window.location.href = 'kakaotalk://inappbrowser/close';
              }

              if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
                window.location.href = 'kakaotalk://web/close';
              }
            },
          },
          {
            label: '다시 시도',
            variant: 'primary',
            onClick: () => {
              window.location.replace(currentUrl);
            },
          },
        ]}
      />
    );

  return (
    <section className='patient-verify'>
      {/* 헤더 영역 */}
      <header className='patient-verify__header'>
        <h2 className='patient-verify__title'>
          케어노트 이용을 위해
          <br />
          환자 정보를 확인할게요
        </h2>
        <p className='patient-verify__desc'>
          케어노트는 {hspNm}에서 제공하는 서비스이며, 입력하신 정보는 환자 정보 확인을 위한 용도로만 사용돼요.
        </p>
      </header>

      {/* 입력 폼 */}
      <form className='patient-verify__form'>
        {/* 이름 */}
        <div className='patient-verify__field'>
          <label className='patient-verify__label'>이름</label>
          <div className={`patient-verify__block patient-verify__input${isForeigner ? '' : '--readonly'}`}>
            <input
              type='text'
              className='patient-verify__input'
              disabled={!isForeigner}
              value={usrNm || ''}
              onChange={(e) => setUsrNm(e.target.value)}
            />
          </div>
        </div>

        {/* 주민등록번호 */}
        <div className='patient-verify__field'>
          <label className='patient-verify__label'>{isForeigner ? '외국인등록번호' : '주민등록번호'}</label>

          <div className='patient-verify__rrn'>
            <div className='patient-verify__rrn-front patient-verify__input--readonly'>
              <input
                type='text'
                className='patient-verify__input'
                placeholder={formattedPtBrdy}
                maxLength={6}
                disabled={true}
              />
            </div>
            <span className='patient-verify__dash'>-</span>

            <div className='patient-verify__rrn-back'>
              <input
                type='text'
                className='patient-verify__input'
                placeholder='뒷자리'
                inputMode='numeric'
                value={usrRrnBackDisplayValue}
                maxLength={20}
                onChange={handleRrnBackChange}
              />
              <button
                type='button'
                className={`patient-verify__eye${showPassword ? '' : '--off'}`}
                aria-label='주민등록번호 보기'
                onClick={() => {
                  setShowPassword(!showPassword);
                  if (!showPassword) {
                    setUsrRrnBackDisplayValue(usrRrnBackRealValue);
                  } else {
                    setUsrRrnBackDisplayValue('*'.repeat(usrRrnBackRealValue.length));
                  }
                }}
              />
            </div>
          </div>

          <p className='patient-verify__helper'>
            {isForeigner ? '외국인등록번호' : '주민등록번호'} 뒷자리를 모두 입력해 주세요.
          </p>
        </div>
      </form>
      {/* 하단 버튼 */}
      <BottomButton
        disabled={btnDisabled}
        variant={btnDisabled ? 'disabled' : 'primary'}
        label='환자 정보 조회하기'
        onClick={handlePostKareNotePatients}
      />
    </section>
  );
};

